import { useCallback, useContext, useEffect } from "react";

import { useMutation, useQuery } from "@apollo/client";

import { PaymentState } from "@/autoGeneratedGlobalTypes";
import { UserContextType } from "@/contexts/User/types";
import UserContext from "@/contexts/User/UserContext";

import { UtmAnalysisEventEnum, useManageUtmData } from "../useManageUtmData";

import { pollingGetPaymentState, pollingGetPaymentStateVariables, pollingGetPaymentState_getMyProfile_balance, pollingGetPaymentState_getPayment } from "./graphql/__generated__/pollingGetPaymentState";
import { setDefaultCardAfterPolling, setDefaultCardAfterPollingVariables } from "./graphql/__generated__/setDefaultCardAfterPolling";
import { POLLING_GET_PAYMENT_STATE } from "./graphql/POLLING_GET_PAYMENT_STATE";
import { SET_DEFAULT_CARD_AFTER_POLLING } from "./graphql/SET_DEFAULT_CARD_AFTER_POLLING";
import { processRobokassaPaymentLinks } from "./utils";

// Хук usePaymentPolling выполняет следующие функции:
// 1. Отслеживает статус незавершенных платежей через GraphQL запрос pollingGetPaymentState
// 2. Автоматически запускает поллинг каждые 2 секунды при наличии unfinishedPaymentId
// 3. Обрабатывает успешные платежи:
//    - Обновляет баланс пользователя в UserContext
//    - Устанавливает карту по умолчанию
//    - Отправляет данные в маркетинговые системы
//    - Обрабатывает ссылки Робокассы
//    - Вызывает callback функции успешного платежа
// 4. Обрабатывает неудачные платежи:
//    - Обрабатывает ссылки Робокассы для неудачных платежей
//    - Вызывает callback функции неудачного платежа
// 5. Автоматически останавливает поллинг при завершении платежа
// 6. Возвращает функции для управления платежами и состояние загрузки

export const usePaymentPolling = () => {
  const {
    setBalance,
    unfinishedPaymentId,
    setUnfinishedPaymentId,
    setFixedPaymentInfo,
    setCurrentPaymentData,
    successPaymentCallback,
    failPaymentCallback,
    setSuccessPaymentCallback,
    setFailPaymentCallback,
  } = useContext<UserContextType>(UserContext);
  const { saveUtmData } = useManageUtmData();
  const {
    data: paymentStatusData,
    loading: loadingPaymentStatus,
    error: paymentStatusError,
    stopPolling,
    startPolling,
  } = useQuery<
    pollingGetPaymentState,
    pollingGetPaymentStateVariables>(POLLING_GET_PAYMENT_STATE, {
      variables: { paymentID: unfinishedPaymentId as number },
      fetchPolicy: "no-cache",
      skip: !unfinishedPaymentId,
    });

  const [
    setDefaultCard,
  ] = useMutation<setDefaultCardAfterPolling, setDefaultCardAfterPollingVariables>(SET_DEFAULT_CARD_AFTER_POLLING, { errorPolicy: "all" });

  useEffect(() => {
    if (unfinishedPaymentId) {
      startPolling(2000);
    } else {
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [unfinishedPaymentId, startPolling, stopPolling]);

  // set_default_Card
  const setDefaultCardHandler = useCallback((data: pollingGetPaymentState_getPayment) => {
    const { card } = data;
    if (card?.id) {
      setDefaultCard({ variables: { cardID: card.id } });
    }
  }, [setDefaultCard]);

  // update_balance
  const setBalanceHandler = useCallback((clientBalanceData: pollingGetPaymentState_getMyProfile_balance) => {
    setBalance({ amount: clientBalanceData.amount, currency: clientBalanceData.currency });
  }, [setBalance]);

  // send_data_for_marketing
  const sendMarketingSuccessPaymentHandler = useCallback((paymentData: pollingGetPaymentState_getPayment) => {
    const { id } = paymentData;

    saveUtmData({
      eventType: UtmAnalysisEventEnum.PaymentSucceded,
      paymentID: id,
    });
  }, [saveUtmData]);

  // SUCCESS PAYMENT
  const onSuccessHandler = useCallback((pollingData: pollingGetPaymentState) => {
    const { link: linkToPageAfterPaymentWithRobokassa } = pollingData.getPayment;
    setBalanceHandler(pollingData.getMyProfile.balance);
    setDefaultCardHandler(pollingData.getPayment);
    sendMarketingSuccessPaymentHandler(pollingData.getPayment);

    if (linkToPageAfterPaymentWithRobokassa) { // Ссылка после оплаты через карту Робокассы.
      processRobokassaPaymentLinks({
        link: linkToPageAfterPaymentWithRobokassa,
        dataAboutPayment: pollingData.getPayment,
        statusPayment: PaymentState.Success,
      });
    }

    if (!linkToPageAfterPaymentWithRobokassa && successPaymentCallback) {
      successPaymentCallback(pollingData.getPayment);
    }
    setUnfinishedPaymentId(null);
    setFixedPaymentInfo({
      isFixPayment: false,
      productId: "",
    });
    setFailPaymentCallback(() =>
      () => {});
    setSuccessPaymentCallback(() =>
      () => {});
  }, [setBalanceHandler, setDefaultCardHandler, sendMarketingSuccessPaymentHandler,
    setFailPaymentCallback, setFixedPaymentInfo, setSuccessPaymentCallback,
    setUnfinishedPaymentId, successPaymentCallback]);

  // FAIL PAYMENT
  const onFailHandler = useCallback((dataAboutPayment: pollingGetPaymentState) => {
    const { link: linkToPageAfterPaymentWithRobokassa } = dataAboutPayment.getPayment;

    if (linkToPageAfterPaymentWithRobokassa) { // Ссылка после оплаты через карту Робокассы.
      processRobokassaPaymentLinks({
        link: linkToPageAfterPaymentWithRobokassa,
        dataAboutPayment: dataAboutPayment.getPayment,
        statusPayment: PaymentState.Failed,
      });
    }
    if (failPaymentCallback) {
      failPaymentCallback(dataAboutPayment.getPayment);
    }

    setFixedPaymentInfo({
      isFixPayment: false,
      productId: "",
    });
    setUnfinishedPaymentId(null);
    setFailPaymentCallback(() =>
      () => {});
    setSuccessPaymentCallback(() =>
      () => {});
  }, [failPaymentCallback, setFixedPaymentInfo,
    setUnfinishedPaymentId, setFailPaymentCallback, setSuccessPaymentCallback]);

  useEffect(() => {
    if (paymentStatusData && !loadingPaymentStatus && !paymentStatusError) {
      const { state } = paymentStatusData.getPayment;

      setCurrentPaymentData(paymentStatusData.getPayment);

      if (state === PaymentState.Success) {
        onSuccessHandler(paymentStatusData);
      }

      if (state === PaymentState.Failed) {
        onFailHandler(paymentStatusData);
      }
    }
  }, [loadingPaymentStatus,
    onFailHandler,
    onSuccessHandler,
    paymentStatusData,
    paymentStatusError,
    setFailPaymentCallback,
    setFixedPaymentInfo,
    setSuccessPaymentCallback,
    setCurrentPaymentData,
    setUnfinishedPaymentId]);

  return {
    setUnfinishedPaymentId,
    pollingLoading: loadingPaymentStatus,
  };
};
